import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { equals } from '../../../../base/redux/functions';
import { updateSettings } from '../../../../base/settings/actions';
import ContextMenu from '../../../../base/ui/components/web/ContextMenu';
import ContextMenuItem from '../../../../base/ui/components/web/ContextMenuItem';
import ContextMenuItemGroup from '../../../../base/ui/components/web/ContextMenuItemGroup';
import { checkBlurSupport, checkVirtualBackgroundEnabled } from '../../../../virtual-background/functions';
import { openSettingsDialog } from '../../../actions';
import { SETTINGS_TABS } from '../../../constants';
import { createLocalVideoTracks } from '../../../functions.web';
import { withPixelLineHeight } from "../../../../base/styles/functions.web";
import { IconVideo, IconCheck } from "../../../../base/icons/svg";
const useStyles = makeStyles()(theme => {
    return {
        container: {
            width: 300,
            maxHeight: "calc(100dvh - 100px)",
            overflow: "auto",
            margin: 0,
            marginBottom: theme.spacing(1),
            position: "relative",
            right: "auto"
        },
        list: {
            margin: 0,
            padding: 0,
            listStyleType: "none"
        },
        entryText: {
            "&.left-margin": {
                marginLeft: "36px"
            }
        },
        previewEntry: {
            cursor: "pointer",
            position: "relative",
            margin: "0 7px",
            marginBottom: theme.spacing(1),
            borderRadius: theme.shape.borderRadius,
            boxSizing: "border-box",
            overflow: "hidden",
            "&:last-child": {
                marginBottom: 0
            }
        },
        selectedEntry: {
            border: `2px solid ${theme.palette.action01Hover}`
        },
        previewVideo: {
            height: "100%",
            width: "100%",
            objectFit: "cover"
        },
        error: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            position: "absolute"
        },
        labelContainer: {
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            maxWidth: "100%",
            zIndex: 2,
            padding: theme.spacing(2)
        },
        label: {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            borderRadius: "4px",
            padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
            color: theme.palette.text01,
            ...withPixelLineHeight(theme.typography.labelBold),
            width: "fit-content",
            maxwidth: `calc(100% - ${theme.spacing(2)} - ${theme.spacing(2)})`,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
        },
        checkboxContainer: {
            padding: "10px 14px"
        }
    };
});
const stopPropagation = (e) => {
    e.stopPropagation();
};
const VideoSettingsContent = ({ changeFlip, currentCameraDeviceId, disableLocalVideoFlip, localFlipX, selectBackground, setVideoInputDevice, toggleVideoSettings, videoDeviceIds, visibleVirtualBackground }) => {
    const _componentWasUnmounted = useRef(false);
    const [trackData, setTrackData] = useState(new Array(videoDeviceIds.length).fill({
        jitsiTrack: null
    }));
    const { t } = useTranslation();
    const videoDevicesRef = useRef(videoDeviceIds);
    const trackDataRef = useRef(trackData);
    const { classes, cx } = useStyles();
    /**
     * Toggles local video flip state.
     *
     * @returns {void}
     */
    const _onToggleFlip = useCallback(() => {
        changeFlip(!localFlipX);
    }, [localFlipX, changeFlip]);
    /**
     * Destroys all the tracks from trackData object.
     *
     * @param {Object[]} tracks - An array of tracks that are to be disposed.
     * @returns {Promise<void>}
     */
    const _disposeTracks = (tracks) => {
        tracks.forEach(({ jitsiTrack }) => {
            jitsiTrack?.dispose();
        });
    };
    /**
     * Creates and updates the track data.
     *
     * @returns {void}
     */
    const _setTracks = async () => {
        _disposeTracks(trackData);
        const newTrackData = await createLocalVideoTracks(videoDeviceIds, 5000);
        // In case the component gets unmounted before the tracks are created
        // avoid a leak by not setting the state
        if (_componentWasUnmounted.current) {
            _disposeTracks(newTrackData);
        }
        else {
            setTrackData(newTrackData);
            trackDataRef.current = newTrackData;
        }
    };
    /**
     * Returns the click handler used when selecting the video preview.
     *
     * @param {string} deviceId - The id of the camera device.
     * @returns {Function}
     */
    const _onEntryClick = (deviceId) => () => {
        setVideoInputDevice(deviceId);
        toggleVideoSettings();
    };
    const _renderPreviewEntry = (data, index) => {
        const { error, jitsiTrack, deviceId } = data;
        const isSelected = deviceId === currentCameraDeviceId;
        const key = `vp-${index}`;
        const tabIndex = "0";
        if (error) {
            return ((React.createElement("div", { className: classes.previewEntry, key: key, tabIndex: -1 },
                React.createElement("div", { className: classes.error }, t(error)))));
        }
        const previewProps = {
            key,
            tabIndex
        };
        const videoInputs = (APP?.store?.getState()?.["features/base/devices"]?.availableDevices?.videoInput) || [];
        const enumLabel = videoInputs.find((d) => d.deviceId === deviceId)?.label;
        const label = enumLabel || jitsiTrack?.getTrackLabel() || "Camera";
        if (isSelected) {
            previewProps["aria-checked"] = true;
        }
        else {
            previewProps.onClick = () => {
                try {
                    _disposeTracks?.(trackDataRef?.current || []);
                }
                catch { }
                const handler = _onEntryClick(deviceId);
                return typeof handler === "function" ? handler() : undefined;
            };
            previewProps.onKeyPress = (e) => {
                if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    previewProps.onClick();
                }
            };
        }
        { }
        return (label && (React.createElement("li", { ...previewProps, role: "radio" },
            React.createElement(ContextMenuItem, { text: label, selected: isSelected, icon: isSelected ? IconCheck : undefined, textClassName: cx(classes.entryText, !isSelected && "left-margin") }))));
    };
    useEffect(() => {
        _setTracks();
        return () => {
            _componentWasUnmounted.current = true;
            _disposeTracks(trackDataRef.current);
        };
    }, []);
    useEffect(() => {
        if (!equals(videoDeviceIds, videoDevicesRef.current)) {
            _setTracks();
            videoDevicesRef.current = videoDeviceIds;
        }
    }, [videoDeviceIds]);
    const videoHeaderID = "video_settings_header";
    return ((React.createElement(ContextMenu, { "aria-labelledby": "video-settings-button", className: classes.container, hidden: false, id: "video-settings-dialog", inDrawer: true, tabIndex: -1 },
        React.createElement(ContextMenuItemGroup, null,
            React.createElement(ContextMenuItem, { header: true, accessibilityLabel: t("settings.video"), icon: IconVideo, id: videoHeaderID, text: t("settings.video") }),
            React.createElement("ul", { className: classes.list, "aria-labelledby": videoHeaderID, role: "radiogroup", tabIndex: -1 }, trackData.map((data, i) => _renderPreviewEntry(data, i)))))));
};
const mapStateToProps = (state) => {
    const { disableLocalVideoFlip } = state['features/base/config'];
    const { localFlipX } = state['features/base/settings'];
    return {
        disableLocalVideoFlip,
        localFlipX: Boolean(localFlipX),
        visibleVirtualBackground: checkBlurSupport()
            && checkVirtualBackgroundEnabled(state)
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        selectBackground: () => dispatch(openSettingsDialog(SETTINGS_TABS.VIRTUAL_BACKGROUND)),
        changeFlip: (flip) => {
            dispatch(updateSettings({
                localFlipX: flip
            }));
        }
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(VideoSettingsContent);
